import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const minimalZen: TemplateRegistration = {
  meta: {
    id: 'minimal-zen',
    name: 'Minimal Zen',
    category: 'Minimal',
    description: 'Ultra-clean design with generous whitespace and Japanese-inspired restraint',
    previewImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const galleryImages = photos.slice(0, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Noto Serif', 'Noto Sans']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="wrap-narrow">
          <div class="review-stars">${renderStars(Math.round(rating))}</div>
          <p class="review-label">${rating.toFixed(1)} &middot; ${reviewCount} reviews</p>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-section">
        <div class="wrap-narrow">
          <p class="promo-label">${promoDiscount}</p>
          <p class="promo-title">${promoTitle}</p>
          <p class="promo-desc">${promoDesc}</p>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section">
        <div class="wrap-narrow about-centered">
          <hr class="decorative-rule">
          <h2>About</h2>
          <p class="body-text">${safeAbout}</p>
          <hr class="decorative-rule">
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceItems = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-item">
            <div class="service-header">
              <h3>${sName}</h3>
              ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
            </div>
            <p>${sDesc}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section">
        <div class="wrap-narrow">
          <div class="section-dot"></div>
          <h2>Services</h2>
          <div class="services-grid">${serviceItems}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`).join('');
      sections.push(`
      <section id="gallery" class="section">
        <div class="wrap">
          <div class="section-dot"></div>
          <h2>Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const t = data.testimonials[0];
      const tName = sanitizeText(t.name);
      const tContent = sanitizeText(t.content);
      const tRole = t.role ? sanitizeText(t.role) : '';
      sections.push(`
      <section id="testimonials" class="section">
        <div class="wrap-narrow">
          <div class="section-dot"></div>
          <h2>Kind Words</h2>
          <div class="testimonial-featured">
            <blockquote>&ldquo;${tContent}&rdquo;</blockquote>
            <cite>
              ${tName}${tRole ? `, ${tRole}` : ''}
              ${t.rating ? `<span class="t-stars">${renderStars(t.rating)}</span>` : ''}
            </cite>
          </div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3>${q}</h3>
            <p>${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section">
        <div class="wrap-narrow">
          <div class="section-dot"></div>
          <h2>Questions</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div>
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-val">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section">
        <div class="wrap-narrow">
          <div class="section-dot"></div>
          <h2>Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section">
        <div class="wrap-narrow">
          <div class="section-dot"></div>
          <h2>Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section">
        <div class="wrap-narrow">
          <div class="section-dot"></div>
          <h2>Contact</h2>
          <div class="contact-card-wrap">
            <div class="contact-block">
              ${enabledFeatures.has('phone-display') && phone ? `<p class="contact-line">${getIconSvg('phone')} <a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></p>` : ''}
              ${enabledFeatures.has('address-map') && address ? `<p class="contact-line">${getIconSvg('map-pin')} ${sanitizeText(address)}</p>` : ''}
              ${enabledFeatures.has('address-map') && googleMapsUrl ? `<p><a class="subtle-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View map &rarr;</a></p>` : ''}
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Name" required>
              <input type="email" placeholder="Email" required>
              <textarea placeholder="Your message" rows="5" required></textarea>
              <button type="submit">Send</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --bg: #fafaf9; --text: #44403c; --text-light: #78716c; --accent: #a8a29e;
      --border: #e7e5e4; --font-heading: 'Noto Serif', serif; --font-body: 'Noto Sans', sans-serif;
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; font-size: 16px; }
    .wrap { max-width: 960px; margin: 0 auto; padding: 0 24px; }
    .wrap-narrow { max-width: 640px; margin: 0 auto; padding: 0 24px; }
    a { color: var(--text); transition: color .2s; }
    a:hover { color: var(--accent); }

    /* NAV */
    .navbar { padding: 32px 0; border-bottom: 1px solid var(--border); }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; max-width: 960px; margin: 0 auto; padding: 0 24px; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.15rem; color: var(--text); letter-spacing: .5px; }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { color: var(--text-light); font-size: .8rem; letter-spacing: 1.5px; text-transform: lowercase; text-decoration: none; }
    .nav-links a:hover { color: var(--text); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 22px; height: 1.5px; background: var(--text); margin: 5px 0; }

    /* HERO - asymmetric two-column 70/30 */
    .hero { padding: 120px 24px 100px; max-width: 960px; margin: 0 auto; display: flex; gap: 0; align-items: stretch; }
    .hero-main { flex: 0 0 70%; display: flex; flex-direction: column; justify-content: center; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2rem, 5vw, 3.2rem); font-weight: 400; line-height: 1.3; margin-bottom: 24px; color: var(--text); }
    .hero-sub { font-size: .95rem; color: var(--text-light); max-width: 480px; margin-bottom: 32px; }
    .hero-cta { display: inline-block; padding: 12px 32px; border: 1px solid var(--text); color: var(--text); font-size: .8rem; letter-spacing: 2px; text-transform: lowercase; text-decoration: none; transition: background .3s, color .3s; }
    .hero-cta:hover { background: var(--text); color: var(--bg); }
    .hero-accent-col { flex: 0 0 30%; display: flex; align-items: center; justify-content: center; border-left: 1px solid var(--border); }
    .hero-accent-label { writing-mode: vertical-rl; text-orientation: mixed; font-family: var(--font-heading); font-size: .75rem; letter-spacing: 4px; text-transform: lowercase; color: var(--accent); }

    /* SECTIONS */
    .section { padding: 100px 0; border-top: 1px solid var(--border); }
    .section h2 { font-family: var(--font-heading); font-size: 1.5rem; font-weight: 400; margin-bottom: 40px; color: var(--text); }
    .section-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); margin-bottom: 24px; }
    .body-text { color: var(--text-light); line-height: 1.9; font-size: .95rem; }
    .about-centered { text-align: center; }
    .decorative-rule { border: none; height: 1px; background: var(--accent); width: 120px; margin: 24px auto; }

    /* SERVICES - 2-col grid with thin-border cards */
    .services-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; }
    .service-item { padding: 28px; border: 1px solid var(--border); }
    .service-header { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
    .service-item h3 { font-family: var(--font-heading); font-size: 1.05rem; font-weight: 400; }
    .service-price { color: var(--accent); font-size: .9rem; }
    .service-item p { color: var(--text-light); font-size: .9rem; }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
    .gallery-item img { width: 100%; height: 220px; object-fit: cover; display: block; filter: saturate(.85); transition: filter .3s; }
    .gallery-item img:hover { filter: saturate(1); }

    /* TESTIMONIALS - single large featured quote */
    .testimonial-featured { padding: 64px 0; text-align: center; }
    .testimonial-featured blockquote { font-family: var(--font-heading); font-size: clamp(1.2rem, 3vw, 1.6rem); font-style: italic; font-weight: 400; color: var(--text); line-height: 1.8; margin-bottom: 24px; max-width: 560px; margin-left: auto; margin-right: auto; }
    .testimonial-featured cite { font-style: normal; font-size: .85rem; color: var(--text-light); }
    .t-stars { display: block; margin-top: 8px; }
    .star { color: var(--border); font-size: .9rem; }
    .star.filled { color: var(--accent); }

    /* GOOGLE REVIEWS */
    .google-reviews { padding: 32px 0; text-align: center; border-top: 1px solid var(--border); }
    .review-label { color: var(--text-light); font-size: .85rem; margin-top: 8px; }
    .review-stars .star { font-size: 1rem; }

    /* PROMO */
    .promo-section { padding: 48px 0; text-align: center; border-top: 1px solid var(--border); }
    .promo-label { font-size: .75rem; letter-spacing: 3px; text-transform: uppercase; color: var(--accent); margin-bottom: 8px; }
    .promo-title { font-family: var(--font-heading); font-size: 1.15rem; margin-bottom: 8px; }
    .promo-desc { font-size: .9rem; color: var(--text-light); }

    /* FAQ */
    .faq-list { display: flex; flex-direction: column; }
    .faq-item { padding: 28px 0; border-bottom: 1px solid var(--border); }
    .faq-item h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 400; margin-bottom: 12px; }
    .faq-item p { color: var(--text-light); font-size: .9rem; line-height: 1.8; }

    /* PRICE LIST */
    .price-list { display: flex; flex-direction: column; }
    .price-row { display: flex; justify-content: space-between; align-items: baseline; padding: 20px 0; border-bottom: 1px solid var(--border); gap: 16px; }
    .price-row h3 { font-family: var(--font-heading); font-size: 1rem; font-weight: 400; margin-bottom: 4px; }
    .price-row p { color: var(--text-light); font-size: .85rem; }
    .price-val { font-family: var(--font-heading); font-size: 1.1rem; color: var(--text); white-space: nowrap; }

    /* HOURS */
    .hours-list { list-style: none; }
    .hours-list li { padding: 12px 0; border-bottom: 1px solid var(--border); color: var(--text-light); font-size: .9rem; }

    /* CONTACT - centered card */
    .contact-card-wrap { max-width: 480px; margin: 0 auto; border: 1px solid var(--border); padding: 40px 32px; }
    .contact-block { margin-bottom: 32px; }
    .contact-line { display: flex; align-items: center; gap: 10px; color: var(--text-light); font-size: .9rem; margin-bottom: 8px; }
    .contact-line .icon-char { font-size: 16px; line-height: 1; color: var(--accent); }
    .contact-line a { color: var(--text); text-decoration: none; }
    .subtle-link { color: var(--accent); font-size: .85rem; text-decoration: none; }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: transparent; border: none; border-bottom: 1px solid var(--border); padding: 12px 0; font-family: var(--font-body); font-size: .9rem; color: var(--text); outline: none; transition: border-color .2s; border-radius: 0; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--text); }
    .contact-form input::placeholder, .contact-form textarea::placeholder { color: var(--accent); }
    .contact-form button { align-self: center; padding: 12px 32px; background: transparent; border: 1px solid var(--text); color: var(--text); font-family: var(--font-body); font-size: .8rem; letter-spacing: 2px; text-transform: lowercase; cursor: pointer; transition: background .3s, color .3s; }
    .contact-form button:hover { background: var(--text); color: var(--bg); }

    /* FOOTER */
    .footer { padding: 48px 0; text-align: center; border-top: 1px solid var(--border); }
    .footer p { font-size: .8rem; color: var(--text-light); letter-spacing: .5px; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: var(--bg, #fff); padding: 16px 24px; gap: 16px; border-bottom: 1px solid var(--border); }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .hero { padding: 80px 24px 60px; flex-direction: column; }
      .hero-main { flex: auto; }
      .hero-accent-col { display: none; }
      .section { padding: 72px 0; }
      .services-grid { grid-template-columns: 1fr; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .price-row { flex-direction: column; gap: 4px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    <div class="hero-main">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">get in touch</a>` : ''}
    </div>
    <div class="hero-accent-col">
      <span class="hero-accent-label">${safeName}</span>
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="wrap-narrow">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
